import { useForm } from "@refinedev/react-hook-form";
import { useCreate, useGetIdentity, useNotification, useTranslation } from "@refinedev/core";
import { useState } from "react";
import { FaInfo } from "react-icons/fa6";
import { FieldValues } from "react-hook-form";

interface BillingInfoFormProps {
  onSuccess?: () => void;
}

export const BillingInfoForm = ({ onSuccess }: BillingInfoFormProps) => {
  const [showForm, setShowForm] = useState(false);
  const { data: identity } = useGetIdentity<{ id?: string; user_id?: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { open } = useNotification();
  const { translate } = useTranslation();
  
  const { mutate: createBillingInfo } = useCreate();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data: FieldValues) => {
    const userId = identity?.id || identity?.user_id;
    

    
    if (!userId) {
      return;
    }
    
    setIsSubmitting(true);
    
    const payload = {
      resource: "user_billing_info",
      values: data,
    };    
    
    createBillingInfo(payload, {
      onSuccess: (result) => {
        open?.({
          type: "success",
          message: translate("page.billing.form.success.message"),
          description: translate("page.billing.form.success.description")
        });
        setIsSubmitting(false);
        onSuccess?.();
      },
      onError: (error) => {
        open?.({
          type: "error",
          message: translate("page.billing.form.error.message"),
          description: translate("page.billing.form.error.description")
        });
        setIsSubmitting(false);
      }
    });
  };

  if (!showForm) {
    return (
      <div className="flex flex-col items-center">
        <div className="card shadow-lg max-w-md">
          <div className="group card-title gap-4 items-center text-base-100 bg-secondary p-4 pl-8 relative overflow-hidden">
            <FaInfo className="w-12 h-12" />
            <h1 className="text-2xl uppercase font-bold w-64">{translate("page.billing.form.title")}</h1>
            <img className="absolute bottom-0 right-0 w-24 -mb-2 -mr-2 group-hover:-mb-1 group-hover:-mr-1 transition-all" src="/img/hachi-corner.png" alt="" />
          </div>
          <div className="card-body bg-base-200">
            <fieldset className="fieldset">
              <legend>{translate("page.billing.form.legend")}</legend>
              <h3 className="text-2xl">{translate("page.billing.form.description")}</h3>
            </fieldset>
            <fieldset className="fieldset my-2">
              <button className="btn btn-primary" onClick={() => setShowForm(true)}>
                {translate("page.billing.form.continue")}
              </button>
            </fieldset>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <h2 className="card-title">{translate("page.billing.form.legend")}</h2>
        <p className="text-sm text-base-content/70 mb-4">
          {translate("page.billing.form.description")}
        </p>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="form-control w-full">
            <label className="label pb-2">
              <span className="label-text font-medium">{translate("page.billing.form.fields.email.label")}</span>
            </label>
            <input
              type="email"
              placeholder={translate("page.billing.form.fields.email.placeholder")}
              className={`input input-bordered w-full ${errors.email ? 'input-error' : 'focus:input-primary'}`}
              {...register("email", { 
                required: translate("page.billing.form.fields.email.required"),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: translate("page.billing.form.fields.email.invalid")
                }
              })}
            />
            {errors.email && (
              <label className="label pt-1">
                <span className="label-text-alt text-error">{errors.email.message?.toString()}</span>
              </label>
            )}
          </div>

          <div className="form-control w-full">
            <label className="label pb-2">
              <span className="label-text font-medium">{translate("page.billing.form.fields.phone.label")}</span>
            </label>
            <input
              type="tel"
              placeholder={translate("page.billing.form.fields.phone.placeholder")}
              className={`input input-bordered w-full ${errors.phone_number ? 'input-error' : 'focus:input-primary'}`}
              {...register("phone_number", { 
                required: translate("page.billing.form.fields.phone.required"),
                minLength: {
                  value: 10,
                  message: translate("page.billing.form.fields.phone.minLength")
                }
              })}
            />
            {errors.phone_number && (
              <label className="label pt-1">
                <span className="label-text-alt text-error">{errors.phone_number.message?.toString()}</span>
              </label>
            )}
          </div>

          <div className="form-control w-full">
            <label className="label pb-2">
              <span className="label-text font-medium">{translate("page.billing.form.fields.document.label")}</span>
            </label>
            <input
              type="text"
              placeholder={translate("page.billing.form.fields.document.placeholder")}
              className={`input input-bordered w-full ${errors.document_id ? 'input-error' : 'focus:input-primary'}`}
              {...register("document_id", { 
                required: translate("page.billing.form.fields.document.required"),
                minLength: {
                  value: 6,
                  message: translate("page.billing.form.fields.document.minLength")
                }
              })}
            />
            {errors.document_id && (
              <label className="label pt-1">
                <span className="label-text-alt text-error">{errors.document_id.message?.toString()}</span>
              </label>
            )}
          </div>

          <div className="card-actions justify-end pt-4 gap-3">
            <button 
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setShowForm(false)}
            >
              {translate("page.billing.form.buttons.back")}
            </button>
            <button 
              type="submit" 
              className={`btn btn-primary ${isSubmitting ? 'loading' : ''}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? translate("page.billing.form.buttons.saving") : translate("page.billing.form.buttons.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};